import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { GridColDef, GridRowModel } from '@mui/x-data-grid-pro';
import sortBy from 'lodash/sortBy';
import { useState } from 'react';

import { ILineItem } from 'common/src/types/interfaces';

import DataTable from 'portal/src/components/common/DataTable';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import ConfirmDeleteDialog from 'portal/src/components/common/tables/ConfirmDeleteDialog';
import { getStartAndEndDateInRowValidation } from 'portal/src/components/common/tables/inRowValidators';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

import LineItemForm from './LineItemForm';
import { mapUnitOfMeasureValues } from './LineItemForm.fields';

import { useBillFormContext } from '../BillFormContext';

const COST_USAGE_PARSER = {
  valueParser: (value) => {
    if (isNaN(parseFloat(value as string))) {
      return 0;
    }
    const decimalDigits = value.split('.')[1] ?? '';
    return parseFloat(value).toFixed(decimalDigits.length);
  },
};

const BillLineItems = () => {
  const {
    accountMeters: accountMeters,
    billIsUsedInAllocations,
    headerForm: { getValues },
    lineItems,
    lineItemSubtypes,
    onLineItemEditStateChange,
    previousBill,
    setLineItems,
  } = useBillFormContext();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [lineItemFormMode, setLineItemFormMode] = useState<
    'add' | 'edit' | null
  >(null);
  const [selectedRowId, setSelectedRowId] = useState<string>('');

  const lineItemsPreviousBill = previousBill.data?.lineItems ?? [];
  const endDate = getValues('endDate');
  const startDate = getValues('startDate');
  const startAndEndDateValidation = getStartAndEndDateInRowValidation();

  const getLineItemRowId = (lineItem: Partial<ILineItem>, index: number) => {
    if (lineItem.id) {
      return String(lineItem.id);
    }

    // use the index to generate an id to avoid duplicates
    return `unsaved-${index}`;
  };

  const isSelectedRow = (lineItem: Partial<ILineItem>, index: number) =>
    getLineItemRowId(lineItem, index) === selectedRowId;

  const normalizeLineItemValuesForSave = (
    lineItem: Partial<ILineItem>,
  ): Partial<ILineItem> => ({
    ...lineItem,
    subTypeId: lineItem.subTypeId ?? undefined,
    description: lineItem.description ?? '',
    unitOfMeasure:
      (lineItem.unitOfMeasure as
        | ILineItem['unitOfMeasure']
        | null
        | undefined) ?? undefined,
    startDate: lineItem.startDate ?? undefined,
    endDate: lineItem.endDate ?? undefined,
    usage: lineItem.usage ?? undefined,
    cost: lineItem.cost ?? undefined,
  });

  const normalizeLineItemValuesForGrid = (lineItem: Partial<ILineItem>) => ({
    ...normalizeLineItemValuesForSave(lineItem),
    // MUI single-select editors require '' for empty values.
    subTypeId: lineItem.subTypeId ?? '',
    unitOfMeasure: lineItem.unitOfMeasure ?? '',
  });

  const tableRows =
    previousBill.isError || accountMeters.isError || lineItemSubtypes.isError
      ? []
      : lineItems.map((lineItem, index) => ({
          ...normalizeLineItemValuesForGrid(lineItem),
          __rowId: getLineItemRowId(lineItem, index),
        }));
  const selectedLineItem = lineItems.find((lineItem, index) =>
    isSelectedRow(lineItem, index),
  );

  const handleRowSelectionChange = ({ ids }) => {
    setSelectedRowId(() => String([...ids][0] ?? ''));
  };

  const handleDelete = () => {
    if (selectedRowId || selectedRowId.toString() === '0') {
      const updatedRows = lineItems.filter(
        (lineItem, index) => !isSelectedRow(lineItem, index),
      );

      setLineItems(updatedRows);
    }
    setSelectedRowId('');
    setOpenDeleteDialog(false);
  };

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleSaveLineItems = (data: Partial<ILineItem>) => {
    handleCloseAddBillLineItemForm();
    setLineItems([...lineItems, data]);
  };

  const removeStaleMeter = (lineItem: Partial<ILineItem>) => {
    if (lineItem.meter?.id === lineItem.meterId) {
      return lineItem;
    }

    const { meter: _, ...lineItemWithoutStaleMeter } = lineItem;
    return lineItemWithoutStaleMeter;
  };

  const handleUpdateLineItems = (data: Partial<ILineItem>) => {
    handleCloseAddBillLineItemForm();

    if (selectedRowId || selectedRowId.toString() === '0') {
      const updatedRows = lineItems.map((lineItem, index) => {
        if (isSelectedRow(lineItem, index)) {
          const updatedLineItem = lineItem?.id
            ? { id: lineItem.id, ...data }
            : { ...lineItem, ...data };

          return removeStaleMeter(updatedLineItem);
        }

        return lineItem;
      });
      setLineItems(updatedRows);
    }
  };

  const openAddBillLineItemForm = () => {
    setLineItemFormMode('add');
  };

  const openEditBillLineItemForm = () => {
    setLineItemFormMode('edit');
  };

  const handleCloseAddBillLineItemForm = () => {
    setLineItemFormMode(null);
    setSelectedRowId('');
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'meterName',
      headerName: 'Meter Name',
      width: 160,
      valueGetter: (_, row) =>
        row.meter?.name ??
        accountMeters.data.find((m) => m.id === row.meterId)?.name,
    },
    {
      field: 'facilityName',
      headerName: 'Facility',
      width: 160,
      valueGetter: (_, row) =>
        row.meter?.parentFacility?.name ??
        accountMeters.data.find((m) => m.id === row.meterId)?.parentFacility
          ?.name,
    },
    { field: 'type', headerName: 'Line Item Type', width: 128 },
    {
      field: 'subTypeId',
      editable: true,
      headerName: 'Subtype',
      width: 128,
      type: 'singleSelect',
      valueOptions: sortBy(
        lineItemSubtypes.data as Array<ILineItem['subType']>,
        (li_st) => li_st.name.toLowerCase(),
      ).map((lineItemSubType) => ({
        label: lineItemSubType.name,
        value: lineItemSubType.id,
      })),
    },
    {
      field: 'description',
      editable: true,
      headerName: 'Description',
      width: 160,
    },
    {
      field: 'unitOfMeasure',
      editable: true,
      headerName: 'Unit',
      width: 80,
      type: 'singleSelect',
      valueOptions: ({ row }) =>
        mapUnitOfMeasureValues(row, accountMeters.data),
    },
    {
      field: 'startDate',
      editable: true,
      ...startAndEndDateValidation.startDate,
      ...addDateColumnProps('Start Date', 108),
    },
    {
      field: 'endDate',
      editable: true,
      ...startAndEndDateValidation.endDate,
      ...addDateColumnProps('End Date', 108),
    },
    {
      field: 'usage',
      editable: true,
      headerName: 'Usage',
      type: 'number',
      width: 100,
      valueFormatter: (value) =>
        numberFormatter(value || 0, {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ...COST_USAGE_PARSER,
    },
    {
      field: 'cost',
      editable: true,
      headerName: 'Cost',
      type: 'number',
      width: 100,
      valueFormatter: (value) => numberFormatter(value || 0),
      ...COST_USAGE_PARSER,
    },
  ];

  const handleRowOrderChange = (params) => {
    const { targetIndex, oldIndex } = params;

    const updatedRows = [...lineItems];
    const [movedItem] = updatedRows.splice(oldIndex, 1);

    updatedRows.splice(targetIndex, 0, movedItem);

    const reorderedRows = updatedRows.map((lineItem, index) => ({
      ...lineItem,
      sortIndex: index,
    }));

    setLineItems(reorderedRows);
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const { __rowId: editedRowId, ...updatedRowValues } = newRow;
    const normalizedUpdatedRowValues = normalizeLineItemValuesForSave(
      updatedRowValues as Partial<ILineItem>,
    );

    const updatedRows: Partial<ILineItem>[] = lineItems.map(
      (lineItem, index) => {
        if (getLineItemRowId(lineItem, index) !== editedRowId) {
          return lineItem;
        }

        return lineItem?.id
          ? { id: lineItem.id, ...normalizedUpdatedRowValues }
          : { ...lineItem, ...normalizedUpdatedRowValues };
      },
    );

    setLineItems(updatedRows);

    return {
      __rowId: editedRowId,
      ...normalizeLineItemValuesForGrid(normalizedUpdatedRowValues),
    };
  };

  return (
    <>
      <DataTable
        actionsConfig={{
          inRowEditConfig: {
            onHasRowsInEditModeChange: onLineItemEditStateChange,
            processRowUpdate,
            btnConfig: (row) => ({
              isEditDisabled: billIsUsedInAllocations,
              isDeleteDisabled: billIsUsedInAllocations,
              overrideEditBtn: () => {
                setSelectedRowId(String(row.__rowId));
                openEditBillLineItemForm();
              },
              onDeleteClick: () => {
                setSelectedRowId(String(row.__rowId));
                setOpenDeleteDialog(true);
              },
            }),
          },
        }}
        isCellEditable={() => !billIsUsedInAllocations}
        isRowNavigation={false}
        checkboxSelection={false}
        columns={gridColumns}
        dataFetchError={
          previousBill.isError ||
          accountMeters.isError ||
          lineItemSubtypes.isError
        }
        loading={
          previousBill.isFetching ||
          accountMeters.isFetching ||
          lineItemSubtypes.isFetching
        }
        onRowSelectionModelChange={handleRowSelectionChange}
        onRowOrderChange={handleRowOrderChange}
        getRowId={(row) => row.__rowId}
        rows={tableRows}
        rowReordering
        sx={{ minHeight: '15rem' }}
        hideFooter
        toolbarSlotProps={{
          children: (
            <Button
              color="primary"
              disabled={billIsUsedInAllocations}
              onClick={openAddBillLineItemForm}
              startIcon={<AddIcon />}
              size="small"
              variant="outlined"
            >
              Add Item
            </Button>
          ),
          showFilters: true,
        }}
      />
      {lineItemFormMode !== null && (
        <LineItemForm
          entity={lineItemFormMode === 'edit' ? selectedLineItem : undefined}
          defaultEndDate={endDate}
          defaultStartDate={startDate}
          lineItemSubtypes={lineItemSubtypes.data}
          lineItemsPreviousBill={lineItemsPreviousBill}
          meters={accountMeters.data}
          open={lineItemFormMode !== null}
          onClose={handleCloseAddBillLineItemForm}
          onSave={
            lineItemFormMode === 'edit'
              ? handleUpdateLineItems
              : handleSaveLineItems
          }
        />
      )}
      <ConfirmDeleteDialog
        showDialog={openDeleteDialog}
        itemName="line item"
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
        isLoading={false}
      />
    </>
  );
};

export default BillLineItems;
